import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  LayoutGrid,
  DollarSign,
  CreditCard,
  TrendingUp,
  Leaf,
  Coffee,
  Package,
} from 'lucide-react';
import { ActivityFeed } from './components/ActivityFeed';
import { OutstandingPaymentsCard } from './components/OutstandingPaymentsCard';
import { DashboardErrorToast } from './components/DashboardErrorToast';
import { QuickActions } from './components/QuickActions';
import { InventoryAlerts, type LowStockItem } from './components/InventoryAlerts';
import { AverageShrinkage } from './components/AverageShrinkage';
import { OutstandingPaymentsModal } from './components/OutstandingPaymentsModal';
import { GreenBeanLotsModal } from './components/GreenBeanLotsModal';
import { RoastedBulkStockModal } from './components/RoastedBulkStockModal';
import { GreenBeanProfileInfographics } from './components/GreenBeanProfileInfographics';
import { FinancialGroupCard, type FinancialGroupItem } from './components/FinancialGroupCard';
import { Modal } from '../../components/common/Modal';
import { CreateSalesOrderForm } from '../sales-orders/CreateSalesOrderForm';
import { CreatePurchaseOrderForm } from '../purchase-orders/CreatePurchaseOrderForm';
import { CreateWorkOrderForm } from '../work-orders/CreateWorkOrderForm';
import { CreateInventoryModal } from '../../components/inventory/CreateInventoryModal';
import { salesOrderService } from '../../services/salesOrderService';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { supplierService } from '../../services/supplierService';
import { workOrderService } from '../../services/workOrderService';
import { useSettingsStore } from '../../store/settingsStore';
import { useAuthStore } from '../../store/authStore';
import type { SalesOrderFormData } from '../../types/salesOrder';
import type { PurchaseOrderFormData } from '../../types/purchaseOrder';
import type { Supplier } from '../../types/supplier';
import type { CreateWorkOrderData, WorkOrder } from '../../types/workOrder';
import { useQueryClient } from '@tanstack/react-query';
import { SUBSCRIPTION_QUERY_KEY } from '../../hooks/useSubscription';
import { DateRangeFilter, type DateRange } from '../../components/common/DateRangeFilter';
import { useToast } from '../../components/common/Toast';
import { DASHBOARD_CARD_KEYS, type DashboardCardKey } from '../../features/dashboard/preferences/constants';
import { useDashboardPreferences } from '../../features/dashboard/preferences/useDashboardPreferences';
import { useDashboardData } from './hooks/useDashboardData';
import { DASHBOARD_MENU_GROUPS } from './constants';
import { DashboardMenuModal } from './components/DashboardMenuModal';
import { ExpensesModal } from './components/ExpensesModal';
import { SupplierModal } from './components/SupplierModal';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDecimal } from '../../utils/format';

type ModalType = 'none' | 'create-so' | 'create-po' | 'create-wo' | 'add-product';

const spanFromVisibleCount = (count: number): string => {
  if (count <= 1) return 'xl:col-span-12';
  if (count === 2) return 'xl:col-span-6';
  return 'xl:col-span-4';
};

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { currencySymbol } = useSettingsStore();
  const { user } = useAuthStore();
  const { can } = usePermissions();
  // Per-widget dashboard read permissions (spec 101).
  const canReadAvgShrinkage = can('dashboard.avg_shrinkage', 'read');
  const canReadOutstanding = can('dashboard.outstanding_payment', 'read');
  const canReadRevenue = can('dashboard.revenue', 'read');
  const canReadExpenses = can('dashboard.expenses', 'read');
  const canReadNetProfit = can('dashboard.net_profit', 'read');
  const canReadGreenBeanValue = can('dashboard.green_bean_value', 'read');
  const canReadRoastedValue = can('dashboard.roasted_value', 'read');
  const canReadFinishedValue = can('dashboard.finished_value', 'read');
  const canReadSpecies = can('dashboard.species', 'read');
  const canReadVarietas = can('dashboard.varietas', 'read');
  const canReadRecentActivities = can('dashboard.recent_activities', 'read');
  const canReadExpiringSoon = can('dashboard.expiring_soon', 'read');
  const canReadLowStock = can('dashboard.low_stock', 'read');
  const canUseQuickActions = can('inventory', 'write') || can('work_orders.roasting', 'write') || can('sales_orders', 'write') || can('purchase_orders', 'write');
  const { showToast } = useToast();
  const { t } = useLanguage();
  const [activeModal, setActiveModal] = useState<ModalType>('none');
  const [poInitialData, setPoInitialData] = useState<PurchaseOrderFormData | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [isOutstandingModalOpen, setIsOutstandingModalOpen] = useState(false);
  const [isExpensesModalOpen, setIsExpensesModalOpen] = useState(false);
  const [isRoastedModalOpen, setIsRoastedModalOpen] = useState(false);
  const [isGreenBeanModalOpen, setIsGreenBeanModalOpen] = useState(false);
  const [greenBeanPage, setGreenBeanPage] = useState(1);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [viewingSupplier, setViewingSupplier] = useState<Supplier | null>(null);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  const [isExpiringWorkOrderModalOpen, setIsExpiringWorkOrderModalOpen] = useState(false);
  const [viewingExpiringWorkOrder, setViewingExpiringWorkOrder] = useState<WorkOrder | null>(null);
  const [expiringWorkOrderLoading, setExpiringWorkOrderLoading] = useState(false);
  const [expiringWorkOrderError, setExpiringWorkOrderError] = useState<string | null>(null);
  const [isDashboardMenuModalOpen, setIsDashboardMenuModalOpen] = useState(false);
  const {
    hiddenCards,
    isSyncing: isPreferencesSyncing,
    setHiddenCards,
  } = useDashboardPreferences({
    userId: user?.id,
    onSyncError: () => {
      showToast(t('dashboard.syncPreferencesWarning'), 'warning');
    },
  });

  const getRangeDates = (range: string) => {
    const now = new Date();
    const toDateInput = (date: Date) => {
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - (offset * 60 * 1000));
      return localDate.toISOString().split('T')[0];
    };

    let start = new Date();
    let end = new Date();

    switch (range) {
      case 'thisWeek': {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start = new Date(now.setDate(diff));
        end = new Date(now.setDate(start.getDate() + 6));
        break;
      }
      case 'thisMonth': {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      }
      case 'lastMonth': {
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      }
      case 'thisQuarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      }
      default:
        return null;
    }
    return { start: toDateInput(start), end: toDateInput(end) };
  };

  const [dateRange, setDateRange] = useState<DateRange>('thisMonth');

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const initialStart = new Date(firstDay.getTime() - (firstDay.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
  const initialEnd = new Date(lastDay.getTime() - (lastDay.getTimezoneOffset() * 60000)).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);

  const handleRangeChange = (range: DateRange) => {
    setDateRange(range);
    if (range === 'custom') return;

    const dates = getRangeDates(range);
    if (dates) {
      setStartDate(dates.start);
      setEndDate(dates.end);
      setGreenBeanPage(1);
    }
  };

  const {
    stats,
    statsLoading,
    statsFetching,
    statsError,
    roastedGroupsSorted,
    roastedGroupsLoading,
    roastedGroupsIsError,
    totalRoastedKg,
    greenBeanProfiles,
    greenBeanProfilesLoading,
    greenBeanProfilesIsError,
    greenBeanProfilesForbidden,
    greenBeanLots,
    greenBeanLotsLoading,
    greenBeanLotsIsError,
    greenBeanTotalKg,
    greenBeanTotalPages,
    poExpenseOverview,
    poExpenseOverviewLoading,
    poExpenseOverviewIsError,
  } = useDashboardData({
    startDate,
    endDate,
    greenBeanPage,
    isExpensesModalOpen,
  });

  const handleCreateSO = async (data: SalesOrderFormData) => {
    try {
      await salesOrderService.create(data);
      await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setActiveModal('none');
    } catch (err) {
      console.error('Failed to create sales order:', err);
      setError(t('dashboard.page.createSalesOrderError'));
    }
  };

  const handleCreatePO = async (data: PurchaseOrderFormData) => {
    try {
      await purchaseOrderService.create(data);
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      await queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
      setActiveModal('none');
    } catch (err) {
      console.error('Failed to create purchase order:', err);
      setError(t('dashboard.page.createPurchaseOrderError'));
    }
  };

  const handleCreateWO = async (data: CreateWorkOrderData, action: 'draft' | 'schedule' | 'complete') => {
    try {
      await workOrderService.create(data);
      await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      await queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      if (action === 'complete') {
        await queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
      }
      setActiveModal('none');
    } catch (err) {
      console.error('Failed to create work order:', err);
      setError(t('dashboard.page.createWorkOrderError'));
    }
  };

  const handleReorder = (item: LowStockItem) => {
    if (item.itemType === 'finished_good' || item.itemType === 'roasted_bulk') {
      navigate('/work-orders?mode=finished_good');
      return;
    }

    setPoInitialData({
      supplier: '',
      orderDate: new Date().toISOString().split('T')[0],
      expectedDeliveryDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0],
      status: 'draft',
      paymentStatus: 'unpaid',
      deliveryStatus: 'shipping',
      internalNotes: '',
      items: [{
        inventoryItemId: item.id,
        name: item.name,
        quantity: Math.max(1, item.min - item.current),
        unitPrice: 0,
        weightReceived: 0,
        sampleItem: false
      }]
    } as PurchaseOrderFormData);
    setActiveModal('create-po');
  };

  const handleExpiringItemClick = async (workOrderId?: string) => {
    if (!workOrderId) return;

    setIsExpiringWorkOrderModalOpen(true);
    setViewingExpiringWorkOrder(null);
    setExpiringWorkOrderError(null);
    setExpiringWorkOrderLoading(true);

    try {
      const workOrder = await workOrderService.getById(workOrderId);
      if (!workOrder) {
        setExpiringWorkOrderError(t('workOrders.page.errors.loadDetailsFailed'));
        return;
      }
      setViewingExpiringWorkOrder(workOrder);
    } catch (err) {
      console.error('Failed to load expiring work order:', err);
      setExpiringWorkOrderError(t('workOrders.page.errors.loadDetailsFailed'));
    } finally {
      setExpiringWorkOrderLoading(false);
    }
  };

  const closeExpiringWorkOrderModal = () => {
    setIsExpiringWorkOrderModalOpen(false);
    setViewingExpiringWorkOrder(null);
    setExpiringWorkOrderError(null);
    setExpiringWorkOrderLoading(false);
  };

  const closeModal = () => {
    setActiveModal('none');
    setError(null);
    setPoInitialData(undefined);
  };

  const closeSupplierModal = () => {
    setIsSupplierModalOpen(false);
    setViewingSupplier(null);
    setSupplierLoading(false);
    setSupplierError(null);
  };

  const handleSupplierClick = async (supplierId?: string) => {
    if (!supplierId) return;

    setIsSupplierModalOpen(true);
    setViewingSupplier(null);
    setSupplierError(null);
    setSupplierLoading(true);

    try {
      const supplier = await supplierService.getById(supplierId);
      if (!supplier) {
        setSupplierError(t('dashboard.page.supplierNotFound'));
        return;
      }
      setViewingSupplier(supplier);
    } catch {
      setSupplierError(t('dashboard.page.supplierLoadError'));
    } finally {
      setSupplierLoading(false);
    }
  };

  const hiddenCardsSet = useMemo(() => {
    const set = new Set(hiddenCards);
    if (set.has(DASHBOARD_CARD_KEYS.financialStats)) {
      DASHBOARD_MENU_GROUPS[0].childKeys.forEach((key) => set.add(key));
    }
    if (set.has(DASHBOARD_CARD_KEYS.greenBeanProfileInfographics)) {
      DASHBOARD_MENU_GROUPS[1].childKeys.forEach((key) => set.add(key));
    }
    if (set.has(DASHBOARD_CARD_KEYS.inventoryAlerts)) {
      set.add(DASHBOARD_CARD_KEYS.inventoryExpiringSoon);
      set.add(DASHBOARD_CARD_KEYS.inventoryLowStock);
    }
    return set;
  }, [hiddenCards]);

  const isCardVisible = (key: DashboardCardKey) => !hiddenCardsSet.has(key);

  const showRevenue = canReadRevenue && isCardVisible(DASHBOARD_CARD_KEYS.financialRevenue);
  const showExpenses = canReadExpenses && isCardVisible(DASHBOARD_CARD_KEYS.financialExpenses);
  const showNetProfit = canReadNetProfit && isCardVisible(DASHBOARD_CARD_KEYS.financialNetProfit);
  const showOutstanding = canReadOutstanding && isCardVisible(DASHBOARD_CARD_KEYS.financialOutstanding);
  const showGreenBean = canReadGreenBeanValue && isCardVisible(DASHBOARD_CARD_KEYS.financialGreenBean);
  const showRoasted = canReadRoastedValue && isCardVisible(DASHBOARD_CARD_KEYS.financialRoasted);
  const showFinished = canReadFinishedValue && isCardVisible(DASHBOARD_CARD_KEYS.financialFinished);
  const showAvgShrinkage = canReadAvgShrinkage && isCardVisible(DASHBOARD_CARD_KEYS.averageShrinkage);
  const showQuickActions = canUseQuickActions && isCardVisible(DASHBOARD_CARD_KEYS.quickActions);
  const showActivityFeed = canReadRecentActivities && isCardVisible(DASHBOARD_CARD_KEYS.activityFeed);
  const showExpiringSoon = canReadExpiringSoon && isCardVisible(DASHBOARD_CARD_KEYS.inventoryExpiringSoon);
  const showLowStock = canReadLowStock && isCardVisible(DASHBOARD_CARD_KEYS.inventoryLowStock);
  const showSpeciesCard = canReadSpecies && isCardVisible(DASHBOARD_CARD_KEYS.greenBeanSpecies);
  const showVarietasCard = canReadVarietas && isCardVisible(DASHBOARD_CARD_KEYS.greenBeanVarietals);
  const showGreenBeanInfographics = showSpeciesCard || showVarietasCard;

  const financialItems: FinancialGroupItem[] = useMemo(() => {
    if (!stats) return [];
    const items: FinancialGroupItem[] = [];
    if (showRevenue) {
      items.push({
        key: 'financial_revenue',
        label: t('dashboard.financialStats.revenue'),
        value: stats.total_sales,
        icon: <DollarSign className="h-5 w-5" />,
        iconColor: 'text-emerald-600',
        iconBgColor: 'bg-emerald-50',
        link: '/sales-orders',
      });
    }
    if (showExpenses) {
      items.push({
        key: 'financial_expenses',
        label: t('dashboard.financialStats.expenses'),
        value: stats.total_purchases,
        icon: <CreditCard className="h-5 w-5" />,
        iconColor: 'text-rose-600',
        iconBgColor: 'bg-rose-50',
        link: '/purchase-orders',
        onClick: () => setIsExpensesModalOpen(true),
      });
    }
    if (showNetProfit) {
      items.push({
        key: 'financial_net_profit',
        label: t('dashboard.financialStats.netProfit'),
        value: stats.net_profit,
        change: stats.net_profit_change,
        icon: <TrendingUp className="h-5 w-5" />,
        iconColor: 'text-blue-600',
        iconBgColor: 'bg-blue-50',
        link: '/reports/general',
      });
    }
    return items;
  }, [stats, showRevenue, showExpenses, showNetProfit, t]);

  const inventoryItems: FinancialGroupItem[] = useMemo(() => {
    if (!stats) return [];
    const items: FinancialGroupItem[] = [];
    if (showGreenBean) {
      items.push({
        key: 'financial_green_bean',
        label: t('dashboard.financialStats.greenBean'),
        value: stats.green_bean_value || 0,
        secondaryText: typeof greenBeanTotalKg === 'number' ? `${formatDecimal(greenBeanTotalKg, 3)} kg` : undefined,
        icon: <Leaf className="h-5 w-5" />,
        iconColor: 'text-green-700',
        iconBgColor: 'bg-green-50',
        link: '/inventory?type=green_bean',
        onClick: () => {
          setGreenBeanPage(1);
          setIsGreenBeanModalOpen(true);
        },
      });
    }
    if (showRoasted) {
      items.push({
        key: 'financial_roasted',
        label: t('dashboard.financialStats.roasted'),
        value: stats.roasted_stock_value || 0,
        secondaryText: typeof totalRoastedKg === 'number' ? `${formatDecimal(totalRoastedKg, 3)} kg` : undefined,
        icon: <Coffee className="h-5 w-5" />,
        iconColor: 'text-orange-700',
        iconBgColor: 'bg-orange-50',
        link: '/inventory?type=roasted_bulk',
        onClick: () => setIsRoastedModalOpen(true),
      });
    }
    if (showFinished) {
      items.push({
        key: 'financial_finished',
        label: t('dashboard.financialStats.finished'),
        value: stats.finished_goods_value || 0,
        icon: <Package className="h-5 w-5" />,
        iconColor: 'text-indigo-600',
        iconBgColor: 'bg-indigo-50',
        link: '/inventory?type=finished_good',
      });
    }
    return items;
  }, [stats, showGreenBean, showRoasted, showFinished, greenBeanTotalKg, totalRoastedKg, t]);

  // Top section visibility
  const showSmallStatRow = showAvgShrinkage || showOutstanding;
  const showLeftColumn = showSmallStatRow || showQuickActions;
  const showFinancialGroup = financialItems.length > 0;
  const showInventoryGroup = inventoryItems.length > 0;
  const topVisibleCount = [showLeftColumn, showFinancialGroup, showInventoryGroup].filter(Boolean).length;
  const topColSpan = spanFromVisibleCount(topVisibleCount);
  const showTopSection = topVisibleCount > 0;

  // Bottom section visibility
  const bottomVisibleCount = [showActivityFeed, showExpiringSoon, showLowStock].filter(Boolean).length;
  const bottomColSpan = spanFromVisibleCount(bottomVisibleCount);
  const showBottomSection = bottomVisibleCount > 0;

  const outstandingClickable = !!stats && stats.outstanding_count > 0;

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('dashboard.page.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('dashboard.page.subtitle')}</p>
        </div>

        <DateRangeFilter
          dateRange={dateRange}
          startDate={startDate}
          endDate={endDate}
          onRangeChange={handleRangeChange}
          onStartDateChange={(date) => {
            setStartDate(date);
            setDateRange('custom');
            setGreenBeanPage(1);
          }}
          onEndDateChange={(date) => {
            setEndDate(date);
            setDateRange('custom');
            setGreenBeanPage(1);
          }}
        />
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="text-xs text-gray-500" data-test-id="dashboard-preferences-status">
          {isPreferencesSyncing ? t('dashboard.page.syncingPreferences') : ''}
        </div>
        <button
          type="button"
          onClick={() => setIsDashboardMenuModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          data-test-id="dashboard-open-menu-modal"
        >
          <LayoutGrid className="h-4 w-4" />
          {t('dashboard.page.dashboardMenu')}
        </button>
      </div>

      {statsError && (
        <div className="bg-red-50/80 backdrop-blur-sm border border-red-100 p-4 rounded-2xl flex items-start gap-3 animate-fade-in">
          <div className="p-2 bg-red-100 rounded-xl">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="mt-1">
            <h3 className="text-sm font-bold text-red-800">{t('dashboard.page.connectionError')}</h3>
            <p className="text-sm text-red-600 mt-0.5">{statsError}</p>
          </div>
        </div>
      )}

      {showTopSection && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch" data-test-id="dashboard-card-financial-stats">
          {showLeftColumn && (
            <div className={`${topColSpan} flex flex-col gap-4`}>
              {showSmallStatRow && (
                <div
                  className={`grid gap-4 ${
                    showAvgShrinkage && showOutstanding ? 'grid-cols-2' : 'grid-cols-1'
                  }`}
                >
                  {showAvgShrinkage && (
                    <div data-test-id="dashboard-card-average-shrinkage">
                      <AverageShrinkage
                        value={stats?.average_shrinkage ?? 0}
                        trend={stats?.shrinkage_trend ?? 'stable'}
                        loading={statsLoading}
                        isFetching={statsFetching}
                      />
                    </div>
                  )}

                  {showOutstanding && (
                    <OutstandingPaymentsCard
                      amount={stats?.outstanding_amount}
                      count={stats?.outstanding_count ?? 0}
                      clickable={outstandingClickable}
                      isFetching={statsFetching}
                      currencySymbol={currencySymbol}
                      onClick={() => setIsOutstandingModalOpen(true)}
                    />
                  )}
                </div>
              )}

              {showQuickActions && (
                <div className="flex-1" data-test-id="dashboard-card-quick-actions">
                  <QuickActions onAction={setActiveModal} />
                </div>
              )}
            </div>
          )}

          {showFinancialGroup && (
            <div className={topColSpan}>
              <FinancialGroupCard
                title={t('dashboard.financialStats.financialOverview')}
                items={financialItems}
                loading={statsLoading}
                isFetching={statsFetching}
                testId="dashboard-card-financial-group"
              />
            </div>
          )}

          {showInventoryGroup && (
            <div className={topColSpan}>
              <FinancialGroupCard
                title={t('dashboard.financialStats.inventoryValue')}
                items={inventoryItems}
                loading={statsLoading}
                isFetching={statsFetching}
                testId="dashboard-card-inventory-group"
              />
            </div>
          )}
        </div>
      )}

      {showGreenBeanInfographics && (
        <div className="rounded-2xl transition-all duration-200 overflow-visible" data-test-id="dashboard-card-green-bean-profile">
          <GreenBeanProfileInfographics
            profiles={greenBeanProfiles}
            loading={greenBeanProfilesLoading}
            isError={greenBeanProfilesIsError}
            isForbidden={greenBeanProfilesForbidden}
            showSpecies={showSpeciesCard}
            showVarietals={showVarietasCard}
          />
        </div>
      )}

      {showBottomSection && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          {showActivityFeed && (
            <div className={bottomColSpan} data-test-id="dashboard-card-activity-feed">
              <ActivityFeed />
            </div>
          )}

          {showExpiringSoon && (
            <div className={bottomColSpan} data-test-id="dashboard-card-inventory-expiring">
              <InventoryAlerts
                onReorder={handleReorder}
                onExpiringItemClick={(item) => {
                  handleExpiringItemClick(item.workOrderId).catch(console.error);
                }}
                showExpiringSoon
                showLowStock={false}
              />
            </div>
          )}

          {showLowStock && (
            <div className={bottomColSpan} data-test-id="dashboard-card-inventory-low-stock">
              <InventoryAlerts
                onReorder={handleReorder}
                showExpiringSoon={false}
                showLowStock
              />
            </div>
          )}
        </div>
      )}

      {isDashboardMenuModalOpen && (
        <DashboardMenuModal
          isOpen
          onClose={() => setIsDashboardMenuModalOpen(false)}
          initialHiddenCards={Array.from(hiddenCardsSet)}
          onSave={(newHiddenCards) => {
            setHiddenCards(newHiddenCards);
            setIsDashboardMenuModalOpen(false);
          }}
        />
      )}

      <OutstandingPaymentsModal
        isOpen={isOutstandingModalOpen}
        onClose={() => setIsOutstandingModalOpen(false)}
        startDate={startDate}
        endDate={endDate}
      />

      <ExpensesModal
        isOpen={isExpensesModalOpen}
        onClose={() => setIsExpensesModalOpen(false)}
        poExpenseOverview={poExpenseOverview}
        loading={poExpenseOverviewLoading}
        isError={poExpenseOverviewIsError}
        currencySymbol={currencySymbol}
        onSupplierClick={(id) => {
          handleSupplierClick(id).catch(console.error);
        }}
      />

      <SupplierModal
        isOpen={isSupplierModalOpen}
        onClose={closeSupplierModal}
        supplier={viewingSupplier}
        loading={supplierLoading}
        error={supplierError}
      />

      <RoastedBulkStockModal
        isOpen={isRoastedModalOpen}
        onClose={() => setIsRoastedModalOpen(false)}
        totalKg={totalRoastedKg}
        loading={roastedGroupsLoading}
        isError={roastedGroupsIsError}
        groups={roastedGroupsSorted}
      />

      <GreenBeanLotsModal
        isOpen={isGreenBeanModalOpen}
        onClose={() => setIsGreenBeanModalOpen(false)}
        currencySymbol={currencySymbol}
        totalKg={greenBeanTotalKg}
        page={greenBeanPage}
        totalPages={greenBeanTotalPages}
        onPrev={() => setGreenBeanPage(p => Math.max(1, p - 1))}
        onNext={() => setGreenBeanPage(p => Math.min(greenBeanTotalPages, p + 1))}
        loading={greenBeanLotsLoading}
        isError={greenBeanLotsIsError}
        lots={greenBeanLots}
      />

      <Modal
        isOpen={isExpiringWorkOrderModalOpen}
        onClose={closeExpiringWorkOrderModal}
        title={viewingExpiringWorkOrder?.workOrderNumber
          ? t('purchasing.lotList.modal.workOrderReadOnly', { number: viewingExpiringWorkOrder.workOrderNumber })
          : t('workOrders.page.viewWorkOrder')}
        maxWidth="7xl"
      >
        {expiringWorkOrderLoading ? (
          <div className="py-10 text-center text-sm text-gray-500">{t('workOrders.page.loading')}</div>
        ) : expiringWorkOrderError ? (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {expiringWorkOrderError}
          </div>
        ) : viewingExpiringWorkOrder ? (
          <CreateWorkOrderForm
            initialData={viewingExpiringWorkOrder}
            onSubmit={() => {}}
            onCancel={closeExpiringWorkOrderModal}
            readOnly
          />
        ) : null}
      </Modal>

      <Modal isOpen={activeModal === 'create-so'} onClose={closeModal} title={t('dashboard.page.modalCreateSalesOrder')} maxWidth="7xl">
        <CreateSalesOrderForm onSubmit={handleCreateSO} onCancel={closeModal} />
      </Modal>

      <Modal isOpen={activeModal === 'create-po'} onClose={closeModal} title={t('dashboard.page.modalCreatePurchaseOrder')} maxWidth="7xl">
        <CreatePurchaseOrderForm initialData={poInitialData} onSubmit={handleCreatePO} onCancel={closeModal} />
      </Modal>

      <Modal isOpen={activeModal === 'create-wo'} onClose={closeModal} title={t('dashboard.page.modalCreateWorkOrder')} maxWidth="7xl">
        <CreateWorkOrderForm onSubmit={handleCreateWO} onCancel={closeModal} />
      </Modal>

      <CreateInventoryModal
        isOpen={activeModal === 'add-product'}
        onClose={closeModal}
        onSuccess={() => setActiveModal('none')}
      />

      {error && <DashboardErrorToast message={error} onClose={() => setError(null)} />}
    </div>
  );
};
